import { useMemo } from "react";
import EmployeeTypes from "../../features/Settings/General/EmployeeTypes";
import UserGroup from "../../features/Settings/General/UserGroup";
import General from "../../features/Settings/General/General";
import JobFamilies from "../../features/Settings/General/JobFamilies";
import JobGroups from "../../features/Settings/General/JobGroups";
import Jobs from "../../features/Settings/General/Jobs";
import PbjCodes from "../../features/Settings/General/PbjCodes";
import Locations from "../../features/Settings/General/Locations";
import OrganisationLevelGroups from "../../features/Settings/General/OrganisationLevelGroups";
import OrganisationLevel from "../../features/Settings/General/OrganisationLevels";
import Roles from "../../features/Settings/General/Roles";
import Tasks from "../../features/Settings/General/Tasks";
import Company from "../../features/Settings/General/Company";
import PayGroup from "../../features/Settings/PayPolicies/PayGroup";
import PayCode from "../../features/Settings/PayPolicies/PayCode";
import EarningGroup from "../../features/Settings/PayPolicies/EarningGroup";
import Holiday from "../../features/Settings/PayPolicies/Holiday";
import TimeOffPayPolicy from "../../features/Settings/PayPolicies/TimeOffRequestCode";
import CalculationGroup from "../../features/Settings/PayPolicies/CalculationGroup";
import Budget from "../../features/Settings/PayPolicies/Budget";
import UltiPro from "../../features/Settings/Integration/UltiPro";
import WhitelistNumber from "../../features/Settings/ClockIn/WhitelistNumber";
import QrCode from "../../features/Settings/ClockIn/QrCode";
import Device from "../../features/Settings/ClockIn/Device";
import Uta from "../../features/Settings/Integration/Uta";
import Union from "../../features/Settings/General/Union";
import TimesheetCustomFields from "../../features/Settings/CustomFields/Timesheet";
import CalculationGroupRule from "../../features/Settings/Rules/CalculationGroup";
import Project from "../../features/Settings/General/Project";
import Reports from "../../features/Settings/Scheduling/Reports";
import JobSchedule from "../../features/Settings/Scheduling/Jobs";
import TimesheetMyTeamConfig from "../../features/Settings/Configuration/Timesheet/MyTeam";
import TimesheetGeneralConfig from "../../features/Settings/Configuration/Timesheet/General";
import MyTimesheetConfig from "../../features/Settings/Configuration/Timesheet/MyTimesheet";
import ManagerViewConfig from "../../features/Settings/Configuration/Timesheet/ManagerView";
import WeeklyConfig from "../../features/Settings/Configuration/Timesheet/Weekly";
import Flyers from "../../features/Settings/ClockIn/Flyers";
import Rate from "../../features/Settings/Configuration/Timesheet/Rate";
import ClockInCustomFields from "../../features/Settings/CustomFields/Clockin";
import Attestation from "../../features/Settings/ClockIn/Attestation";
import Scheduler from "../../features/Settings/General/Scheduler";
import WorkingShift from "../../features/Settings/General/WorkingShift";
import ShiftPremium from "../../features/Settings/PayPolicies/CalculationRules/ShiftPremium";

import ScheduleConfiguration from "../../features/Settings/Configuration/Schedule";
import RequestsConfiguration from "../../features/Settings/Configuration/Requests";
import { useAccessInList } from "../../utils/hooks/access";
import { getViewPermissions } from "../../utils/helpers/access";
import BlackOutCalendar from "../../features/Settings/BlackOutCalendar";
import Groupings from "../../features/Settings/Groupings";
import MealPremium from "../../features/Settings/PayPolicies/CalculationRules/MealPremium";
import ClockException from "../../features/Settings/PayPolicies/CalculationRules/ClocksExceptions";
import NotificationSettings from "../../features/Settings/General/Notifications";
import Gender from "../../features/Settings/UserDemographics/Gender";
import EmploymentType from "../../features/Settings/UserDemographics/EmploymentType";
import Skills from "../../features/Settings/General/Skills";
import Certifications from "../../features/Settings/General/Certifications";
import Licenses from "../../features/Settings/General/Licenses";
import Ethnicity from "../../features/Settings/UserDemographics/Ethnicity";
import AmountPerUnit from "../../features/Settings/PayPolicies/CalculationRules/AmountPerUnit";
import PeopleCustomFields from "../../features/Settings/CustomFields/People";
import TimeOffRule from "../../features/Settings/Rules/TimeOff";
import HolidayRules from "../../features/Settings/Rules/Holiday";
import LocationRule from "../../features/Settings/Rules/Location";
import People from "../../features/Settings/Configuration/ApiConnection/People";

export const useSettingsRoutes = (permissions) => {
	const generalSettings = useMemo(
		() =>
		  permissions?.settings?.general
			? getViewPermissions(permissions?.settings?.general, "settings.general")
			: [],
		[permissions?.settings?.general]
	);

	const { hasAccess: canViewGeneralSettings } = useAccessInList(generalSettings);

	const { hasAccess: canViewClockinSettings } = useAccessInList([
		"device.canView",
		"qrCode.canView",
		"whitelistNumber.canView",
		"settings.flyer.canView"
	]);

	const payPoliciesSettings = useMemo(
		() =>
		  permissions?.settings?.payPolicies
			? getViewPermissions(
			  permissions?.settings?.payPolicies,
			  "settings.payPolicies"
			)
			: [],
		[permissions?.settings?.payPolicies]
	);

	const { hasAccess: canViewPayPolicies } = useAccessInList(payPoliciesSettings);

	const { hasAccess: canViewIntegration } = useAccessInList([
		"settings.integration.ultipro.canView",
		"settings.integration.uta.canView"
	]);

	return useMemo(() => ({
		path: "settings",
		access:
				canViewGeneralSettings ||
				canViewClockinSettings ||
				canViewPayPolicies ||
				canViewIntegration ||
				permissions?.settings?.configuration?.canView ||
				permissions?.settings?.scheduling?.reports?.canView ||
				permissions?.settings?.scheduling?.jobs?.canView ||
				permissions?.settings?.general?.rules?.calculationGroup?.canView ||
				permissions?.settings?.general?.customFields?.canView ||
				permissions?.settings?.general?.organizationLevelGroup?.canView ||
				permissions?.settings?.userDemographic?.canView,
		children: [
			{
				path: "general",
				access: canViewGeneralSettings,
				children: [
					{ index: true, element: <General />, access: permissions?.settings?.general?.canView },
					{
						path: "groups",
						access: permissions?.settings?.general?.organizationLevelGroup.canView,
						children: [
							{
								index: true,
								element: <OrganisationLevelGroups />,
								access: permissions?.settings?.general?.organizationLevelGroup.canView
							},
							{
								path: ":id/levels",
								element: <OrganisationLevel />,
								access: permissions?.settings?.general?.organizationLevelGroup.organizationLevel.canView
							},
						],
					},
					{
						path: "companies",
						element: <Company />,
						access: permissions?.settings?.general?.companies?.canView
					},
					{
						path: "locations",
						element: <Locations />,
						access: permissions?.settings?.general?.locations?.canView
					},
					{
						path: "employee-type",
						element: <EmployeeTypes />,
						access: permissions?.settings?.general?.employeeTypes?.canView
					},
					{
						path: "user-group",
						element: <UserGroup />,
						access: permissions?.settings?.general?.userGroup?.canView
					},
					{
						path: "scheduler",
						element: <Scheduler />,
						access: permissions?.settings?.general?.employeeTypes?.canView
					},
					{
						path: "union",
						element: <Union />,
						access: permissions?.settings?.general?.union?.canView
					},
					{
						path: "projects",
						element: <Project />,
						access: permissions?.settings?.general?.project?.canView
					},
					{
						path: "job-groups",
						element: <JobGroups />,
						access: permissions?.settings?.general?.jobGroups?.canView
					},
					{
						path: "job-families",
						element: <JobFamilies />,
						access: permissions?.settings?.general?.jobFamilies?.canView
					},
					{
						path: "pbj-codes",
						element: <PbjCodes />,
						access: permissions?.settings?.general?.pbjCode?.canView
					},
					{
						path: "jobs",
						element: <Jobs />,
						access: permissions?.settings?.general?.jobs?.canView
					},
					{
						path: "working-shift",
						element: <WorkingShift />,
						access: permissions?.settings?.general.workingShifts?.canView
					},
					{
						path: "roles",
						element: <Roles />,
						access: permissions?.settings?.general?.roles?.canView
					},
					{
						path: "tasks",
						element: <Tasks />,
						access: permissions?.settings?.general?.tasks?.canView
					},
					{
						path: "notification-settings",
						element: <NotificationSettings />,
						access: permissions?.notificationSettings?.canManage,
					},
					{
						path: "skills",
						element: <Skills />,
						access: permissions?.settings?.general?.skill?.canView,
					},
					{
						path: "certifications",
						element: <Certifications />,
						access: permissions?.settings?.general?.certification?.canView
					},
					{
						path: "licenses",
						element: <Licenses />,
						access: permissions?.settings?.general?.license?.canView
					},
				],
			},
			{
				path: "pay-policy",
				access: canViewPayPolicies,
				children: [
					{
						path: "pay-group",
						element: <PayGroup />,
						access: permissions?.settings?.payPolicies?.payGroup?.canView
					},
					{
						path: "pay-code",
						element: <PayCode />,
						access: permissions?.settings?.payPolicies?.payCode?.canView
					},
					{
						path: "earning-group",
						element: <EarningGroup />,
						access: permissions?.settings?.payPolicies?.earningGroup?.canView
					},
					{
						path: "budget",
						element: <Budget />,
						access: permissions?.settings?.payPolicies?.budget?.canView
					},
					{
						path: "holiday-calendar",
						element: <Holiday />,
						access: permissions?.settings?.payPolicies?.holidays?.canView
					},
					{
						path: "time-off",
						element: <TimeOffPayPolicy />,
						access: permissions?.settings?.payPolicies?.timeOff?.canView
					},
					{
						path: "calculation-rules",
						access: permissions?.settings?.payPolicies?.calculationGroup?.canView ,
						children: [
							{
								path: "meal-premium",
								element: <MealPremium />,
								access: permissions?.settings?.payPolicies?.calculationGroup?.canView
							},
							{
								path: "clock-exceptions",
								element: <ClockException />,
								access: permissions?.settings?.payPolicies?.calculationGroup?.canView
							},
							{
								path: "shift-premium",
								element: <ShiftPremium />,
								access: permissions?.settings?.payPolicies?.calculationGroup?.canView
							},
							{
								path: "amount-per-unit",
								element: <AmountPerUnit />,
								access: permissions?.settings?.payPolicies?.calculationGroup?.canView
							},
						],
					},
					{
						path: "calculation-group",
						element: <CalculationGroup />,
						access: permissions?.settings?.payPolicies?.calculationGroup?.canView
					},
				],
			},
			{
				path: "integration",
				access: canViewIntegration,
				children: [
					{
						path: "ultipro",
						element: <UltiPro />,
						access: permissions?.settings?.integration?.ultipro?.canView
					},
					{
						path: "uta",
						element: <Uta />,
						access: permissions?.settings?.integration?.uta?.canView
					},
				],
			},
			{
				path: "clock-in",
				access: canViewClockinSettings,
				children: [
					{
						path: "whitelist-number",
						element: <WhitelistNumber />,
						access: permissions?.whitelistNumber?.canView
					},
					{
						path: "qr-code",
						element: <QrCode />,
						access: permissions?.qrCode?.canView
					},
					{
						path: "device",
						element: <Device />,
						access: permissions?.device?.canView
					},
					{
						path: "flyers",
						element: <Flyers />,
						access: permissions?.settings?.flyer?.canView
					},
					{
						path: "attestation",
						element: <Attestation />,
						access: permissions?.settings?.attestation?.canView
					}
				],
			},
			{
				path: "custom-fields",
				access: permissions?.settings?.general?.customFields?.canView,
				children: [
					{
						path: "timesheet",
						element: <TimesheetCustomFields />,
						access: permissions?.settings?.general?.customFields?.canView
					},
					{
						path: "clock-in",
						element: <ClockInCustomFields />,
						access: permissions?.settings?.general?.customFields?.canView
					},
					{
						path: "people",
						element: <PeopleCustomFields />,
						access: permissions?.settings?.general?.customFields?.canView
					},
				],
			},
			{
				path: "allocation-rules",
				access: permissions?.settings?.general?.rules?.calculationGroup?.canView ,
				children: [
					{
						path: "calculation-group",
						element: <CalculationGroupRule />,
						access: permissions?.settings?.general?.rules?.calculationGroup?.canView
					},
					{
						path: "time-off",
						element: <TimeOffRule />,
						access: permissions?.settings?.general?.rules?.allocationRules?.canView
					},
					{
						path: "holidays",
						element: <HolidayRules />,
						access: permissions?.settings?.general?.rules?.allocationRules?.canView
					},
					{
						path: "locations",
						element: <LocationRule />,
						access: permissions?.settings?.general?.rules?.allocationRules?.canView
					}
				],
			},
			{
				path: "job-scheduling",
				access: permissions?.settings?.scheduling?.reports?.canView ||
						permissions?.settings?.scheduling?.jobs?.canView,
				children: [
					{
						path: "reports",
						element: <Reports />,
						access: permissions?.settings?.scheduling?.reports?.canView,
					},
					{
						path: "jobs",
						element: <JobSchedule />,
						access: permissions?.settings?.scheduling?.jobs?.canView,
					},
				],
			},
			{
				path: "user-demographics",
				access: permissions?.settings?.userDemographic?.canView,
				children: [
					{
						path: "gender",
						element: <Gender />,
						access: permissions?.settings?.userDemographic?.canView,
					},
					{
						path: "ethnicity",
						element: <Ethnicity />,
						access: permissions?.settings?.userDemographic?.canView,
					},
					{
						path: "employment-type",
						element: <EmploymentType />,
						access: permissions?.settings?.userDemographic?.canView,
					},
				],
			},
			{
				path: "black-out-calendar",
				access: permissions?.settings?.configuration?.canView, //TODO permission
				element: <BlackOutCalendar />
			},
			{
				path: "groupings",
				access: permissions?.settings?.groupings?.canView,
				element: <Groupings />
			},
			{
				path: "configuration",
				access: permissions?.settings?.configuration?.canView,
				children: [
					{
						path: "timesheet",
						children: [
							{
								path: "general",
								element: <TimesheetGeneralConfig />,
								access: permissions?.settings?.configuration?.canView,
							},
							{
								path: "team",
								element: <TimesheetMyTeamConfig />,
								access: permissions?.settings?.configuration?.canView,
							},
							{
								path: "manager-view",
								element: <ManagerViewConfig />,
								access: permissions?.settings?.configuration?.canView,
							},
							{
								path: "weekly",
								element: <WeeklyConfig />,
								access: permissions?.settings?.configuration?.canView,
							},
							{
								path: "my",
								element: <MyTimesheetConfig />,
								access: permissions?.settings?.configuration?.canView,
							},
							{
								path: "rate",
								element: <Rate />,
								access: permissions?.settings?.configuration?.canView,
							}
						]
					},
					{
						path: "schedule",
						element: <ScheduleConfiguration />,
						access: permissions?.settings?.configuration?.canView,
					},
					{
						path: "requests",
						element: <RequestsConfiguration />,
						access: permissions?.settings?.configuration?.canView,
					},
					{
						path: "api-connection",
						children: [
							{
								path: "people",
								element: <People />,
								access: permissions?.settings?.configuration?.canView,
							}
						]
					}
				]
			}
		],
	}), [permissions, canViewClockinSettings, canViewGeneralSettings, canViewIntegration, canViewPayPolicies])
}
